import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { SITE, scrollToId } from "@/lib/site";

const NAV_SECTIONS = [
  { label: "Features", id: "features" },
  { label: "Coverage", id: "coverage" },
  { label: "Gallery", id: "gallery" },
  { label: "Reviews", id: "reviews" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 650);
    } else {
      scrollToId(id);
    }
  };

  const goInquiry = () => {
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("inquiry"), 650);
    } else {
      scrollToId("inquiry");
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_1px_0_rgba(15,35,68,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-3"
        >
          <img
            src="/m2-logo.jpg"
            alt="M² Tuitions logo"
            className="h-11 w-11 rounded-xl object-cover ring-1 ring-slate-200"
          />
          <span className="leading-tight">
            <span className="block font-serif text-xl font-extrabold tracking-tight text-navy-dark">
              M² Tuitions
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              {SITE.tagline}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV_SECTIONS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goSection(l.id)}
              className="text-sm font-semibold text-slate-600 transition-colors hover:text-navy"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/careers"
            data-testid="careers-nav-link"
            className="text-sm font-semibold text-slate-600 transition-colors hover:text-navy"
          >
            For Teachers
          </Link>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={SITE.phoneHref}
            data-testid="navbar-call-btn"
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-navy-dark transition hover:border-navy/40 hover:shadow-sm"
          >
            <span className="pulse-dot h-2 w-2 rounded-full bg-teal-600" />
            <Phone className="h-3.5 w-3.5" />
            {SITE.phoneDisplay}
          </a>
          <button
            data-testid="book-demo-nav-btn"
            onClick={goInquiry}
            className="rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-navy-dark hover:shadow-lg"
          >
            Book 2 Free Demos
          </button>
        </div>

        <button
          data-testid="mobile-menu-btn"
          aria-label="Toggle menu"
          className="rounded-xl border border-slate-200 bg-white p-2.5 text-navy-dark lg:hidden"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="border-t border-slate-200 bg-white/95 px-5 pb-6 pt-3 backdrop-blur-xl lg:hidden"
        >
          {NAV_SECTIONS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-link-${l.id}`}
              onClick={() => goSection(l.id)}
              className="block w-full py-3 text-left text-sm font-semibold text-slate-700"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/careers"
            data-testid="mobile-careers-nav-link"
            onClick={() => setOpen(false)}
            className="block py-3 text-sm font-semibold text-slate-700"
          >
            For Teachers
          </Link>
          <div className="mt-3 flex flex-col gap-3">
            <a
              href={SITE.phoneHref}
              data-testid="mobile-call-btn"
              className="flex items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-navy-dark"
            >
              <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
            </a>
            <button
              data-testid="mobile-book-demo-btn"
              onClick={goInquiry}
              className="rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white"
            >
              Book 2 Free Demos
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
